import type { LogsEvent } from '@datadog/browser-logs';
import type { RumEvent } from '@datadog/browser-rum';

/** Owning team, attached to every RUM event and log for filtering and cost attribution in Datadog. */
export const OWNING_TEAM = 'ecom-oneshop';

// A credential parameter (`?token=`, `&bearer=`, `#id_token=` ...), tested on the decoded text.
const CREDENTIAL_PAIR = /[?&#](?:token|access_token|id_token|refresh_token|bearer|code|otp|password|secret|sid|key|registration|verification_code)=/i;
const PERCENT_ESCAPE = /%[0-9a-f]{2}/gi;
const FIRST_QUERY_OR_FRAGMENT = /[?#]/;
// ASM (agent) handoff link `?asm=true&id=<login token>`: `id` is only a credential next to `asm`, so a plain `id=` on
// any other url (order ids, product ids ...) is kept.
const ASM_FLAG = /[?&#]asm(?:[=&#]|$)/i;
const ID_PAIR = /[?&#]id=/i;
// Token exchange `/authenticate/token/<login token>`: the credential is the whole last path segment, up to the next
// `/`, `?`, `#` or whitespace. Quotes and parentheses are valid token characters, so they are part of the segment too.
// The lookahead skips a segment that is already `[redacted]`, so redacting twice changes nothing.
const TOKEN_EXCHANGE_PATH_SOURCE = String.raw`\/authenticate\/token\/(?!\[redacted\])[^/?#\s]+`;
const TOKEN_EXCHANGE_PATH = new RegExp(TOKEN_EXCHANGE_PATH_SOURCE, 'i');
const TOKEN_EXCHANGE_PATH_ALL = new RegExp(TOKEN_EXCHANGE_PATH_SOURCE, 'gi');
const TOKEN_EXCHANGE_REDACTED = '/authenticate/token/[redacted]';
const MAX_DECODE_PASSES = 5;
/**
 * Undoes the encodings a URL can hide a parameter name behind (`%74oken`, `%26`, `&amp;`, nested encoded URLs).
 * Decodes escape by escape, so one malformed `%ZZ` cannot stop the rest from being decoded.
 * Returns `undefined` when the text still changes after the last pass: it is nested too deeply to inspect.
 */
function decodeForInspection(chunk: string): string | undefined {
  let text = chunk.replaceAll('&amp;', '&').replaceAll('\\u0026', '&');
  for (let pass = 0; pass < MAX_DECODE_PASSES; pass++) {
    const decoded = text
      .replace(PERCENT_ESCAPE, (escape) => String.fromCharCode(Number.parseInt(escape.slice(1), 16)))
      .replaceAll('&amp;', '&');
    if (decoded === text) {
      return text;
    }
    text = decoded;
  }
  return undefined;
}

/** Fails closed: text that cannot be fully decoded is treated as carrying a credential. */
function hasCredential(text: string): boolean {
  const decoded = decodeForInspection(text);
  return decoded === undefined || CREDENTIAL_PAIR.test(decoded) || (ASM_FLAG.test(decoded) && ID_PAIR.test(decoded));
}

// Looks at the text as written and, when it can be decoded, as decoded, so a query too deeply encoded to inspect cannot
// hide a token path that sits in plain sight in front of it.
function hasTokenExchangePath(text: string): boolean {
  if (TOKEN_EXCHANGE_PATH.test(text)) {
    return true;
  }
  const decoded = decodeForInspection(text);
  return decoded !== undefined && TOKEN_EXCHANGE_PATH.test(decoded);
}

function redactChunk(input: string): string {
  let chunk = input;
  if (hasTokenExchangePath(chunk)) {
    chunk = chunk.replace(TOKEN_EXCHANGE_PATH_ALL, TOKEN_EXCHANGE_REDACTED);
    // Still there after the replacement: the path hides behind an encoding, so nothing safe is left to keep.
    if (hasTokenExchangePath(chunk)) {
      return '[redacted]';
    }
    // The token segment can swallow the `)` that closes a `(url)` stack frame; put it back.
    if (input.startsWith('(') && input.endsWith(')') && !chunk.endsWith(')')) {
      chunk += ')';
    }
  }
  if (!hasCredential(chunk)) {
    return chunk;
  }
  const cut = chunk.search(FIRST_QUERY_OR_FRAGMENT);
  // Nothing safe to keep when there is no query to cut at, or when the credential is already in the kept prefix
  // (for example a fully percent-encoded URL such as `https%3A%2F%2Fx%2Flogin%3Ftoken%3Dsecret`).
  // The same goes for a token path left in the kept prefix (for example an encoded one in front of a query that cannot be
  // decoded).
  if (cut === -1 || hasCredential(chunk.slice(0, cut)) || hasTokenExchangePath(chunk.slice(0, cut))) {
    return '[redacted]';
  }
  // Keep the paren that closes a `(url)` frame, but only when this chunk opened it, never part of a value.
  const closer = chunk.startsWith('(') && chunk.endsWith(')') ? ')' : '';
  return `${chunk.slice(0, cut)}?[redacted]${closer}`;
}

/**
 * Password reset, OTP and SSO handoff links carry credentials in the query string or fragment, and
 * `defaultPrivacyLevel` does not cover URLs. Each whitespace-separated chunk that carries a credential parameter (in
 * any encoding) keeps its scheme, host and path and loses everything after, so no guess about where a credential
 * value ends is needed, and nothing after the cut is kept (not even a `:line:col` suffix, which cannot be told apart from
 * credential content). Works for URL fields and for free text such as error messages and stack traces.
 */
export function redactCredentials<T extends string | undefined>(text: T): T {
  return (typeof text === 'string' ? text.replace(/\S+/g, redactChunk) : text) as T;
}

export function containsCredential(text: string | undefined): boolean {
  return typeof text === 'string' && redactCredentials(text) !== text;
}

/**
 * The SDK only applies `beforeSend` edits to a fixed list of fields (view.url/referrer/name, error.message/stack/
 * resource.url, resource.url, action.target.name, long-task script urls, ...). `error.handling_stack` and
 * `error.causes` are not on it, so a credential there cannot be scrubbed and the whole event has to be dropped.
 */
export function hasUnscrubbableCredentials(event: RumEvent): boolean {
  if (event.type !== 'error' || !event.error) {
    return false;
  }
  const { handling_stack, causes } = event.error;
  return (
    containsCredential(handling_stack) ||
    (causes ?? []).some((cause) => containsCredential(cause.message) || containsCredential(cause.stack))
  );
}

/** Mutates the event in place, as the Datadog `beforeSend` contract allows, using only fields the SDK honours. */
export function redactEventUrls(event: RumEvent): void {
  if ('view' in event && event.view) {
    event.view.url = redactCredentials(event.view.url);
    event.view.referrer = redactCredentials(event.view.referrer);
    event.view.name = redactCredentials(event.view.name);
    // `view.performance` is typed loosely by some versions of the SDK typings, so go through a narrow local type.
    const lcp = (event.view as { performance?: { lcp?: { resource_url?: string } } }).performance?.lcp;
    if (lcp?.resource_url) {
      lcp.resource_url = redactCredentials(lcp.resource_url);
    }
  }
  if (event.type === 'resource') {
    event.resource.url = redactCredentials(event.resource.url);
  }
  if (event.type === 'action' && event.action?.target) {
    event.action.target.name = redactCredentials(event.action.target.name);
  }
  if (event.type === 'long_task') {
    event.long_task?.scripts?.forEach((script) => {
      script.source_url = redactCredentials(script.source_url);
    });
  }
  if (event.type === 'error' && event.error) {
    event.error.message = redactCredentials(event.error.message);
    event.error.stack = redactCredentials(event.error.stack);
    if (event.error.resource) {
      event.error.resource.url = redactCredentials(event.error.resource.url);
    }
  }
}

/**
 * `beforeSend` for the RUM SDK: drops events whose credentials cannot be scrubbed, redacts the rest in place, and fails
 * closed (the event is not sent) if inspecting or scrubbing the event throws.
 */
export function scrubEvent(event: RumEvent): boolean {
  try {
    if (hasUnscrubbableCredentials(event)) {
      return false;
    }
    redactEventUrls(event);
  } catch {
    return false;
  }
  return true;
}

/**
 * `beforeSend` for the Logs SDK: redacts credentials in the fields it honours (message, error stack, page and request
 * urls), drops logs whose chained `error.causes` carry a credential (that field cannot be edited), and fails closed (the
 * log is not sent) if scrubbing throws.
 */
export function scrubLog(log: LogsEvent): boolean {
  try {
    // `error.causes` (chained errors) is not an editable Logs field, so a credential there cannot be scrubbed and the log
    // has to be dropped, as the RUM path does.
    const causes = (log.error as { causes?: { message?: string; stack?: string }[] } | undefined)?.causes ?? [];
    if (causes.some((cause) => containsCredential(cause.message) || containsCredential(cause.stack))) {
      return false;
    }
    log.message = redactCredentials(log.message);
    if (log.error) {
      log.error.stack = redactCredentials(log.error.stack);
    }
    if (log.view) {
      log.view.url = redactCredentials(log.view.url);
      log.view.referrer = redactCredentials(log.view.referrer);
    }
    const http = (log as { http?: { url?: string } }).http;
    if (http) {
      http.url = redactCredentials(http.url);
    }
  } catch {
    return false;
  }
  return true;
}
